import axios from 'axios';
import Swal from 'sweetalert2';
import {
  resolveApiBase,
  rememberApiBase,
  hasExplicitApiBase,
  DEV_BACKEND_CANDIDATES,
  PROD_BACKEND_CANDIDATES,
} from './apiBase';
import { API_URL } from '../config';
import { axiosErrorMessage as formatAxiosError } from './axiosErrorMessage';

const isProd = process.env.NODE_ENV === 'production';
const DEMO_TOKEN_KEY = 'demo-auth-token';
const DEFAULT_LOCAL_BASE = 'http://localhost:5001';
const RESERVED_PYTHON_BASE = /^https?:\/\/(?:localhost|127\.0\.0\.1|0\.0\.0\.0):5002(?:\/.*)?$/i;

// Check if running on localhost - if so, always use local backend
const isLocalhost = typeof window !== 'undefined' &&
  (window.location?.hostname === 'localhost' || window.location?.hostname === '127.0.0.1');

// Prefer explicit API base from env/runtime, otherwise resolve via heuristics.
const envApiUrl = process.env.REACT_APP_API_URL;
let resolvedBase = envApiUrl || resolveApiBase() || API_URL || '';

// On localhost, prefer the injected dev backend URL; fallback to the default docker backend.
if (isLocalhost) {
  const localBase = envApiUrl || DEFAULT_LOCAL_BASE;
  resolvedBase = RESERVED_PYTHON_BASE.test(localBase) ? DEFAULT_LOCAL_BASE : localBase;

}

// Debug only: console.log('[API] isLocalhost:', isLocalhost, 'isProd:', isProd, 'resolvedBase:', resolvedBase);
const normalizeBase = (value) => (value ? String(value).replace(/\/+$/, '') : '');

const isLocalHost = () => {
  try {
    const host = window.location?.hostname || '';
    return /localhost|127\.0\.0\.1/.test(host);
  } catch (_) {
    return false;
  }
};

const shouldProbeBase = () => {
  if (typeof window === 'undefined') return false;
  if (isProd) return false;
  return isLocalHost();
};

// Discover a reachable dev backend and cache it for the session.
let devBaseChecked = false;
async function ensureDevBase() {
  if (!shouldProbeBase()) return;
  if (devBaseChecked) return;
  devBaseChecked = true;

  const probe = async (base) => {
    try {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 600);
      const res = await fetch(`${base}/api/test-connection`, { signal: ctrl.signal, credentials: 'omit' });
      clearTimeout(timer);
      return res.ok;
    } catch (_) {
      return false;
    }
  };

  if (resolvedBase && !/^\s*$/.test(resolvedBase)) {
    const isLocalBase = /localhost|127\.0\.0\.1/.test(resolvedBase);
    if (isLocalBase && await probe(resolvedBase)) {
      rememberApiBase(resolvedBase);
      return;
    }
  }

  if (DEFAULT_LOCAL_BASE && resolvedBase !== DEFAULT_LOCAL_BASE) {
    if (await probe(DEFAULT_LOCAL_BASE)) {
      resolvedBase = DEFAULT_LOCAL_BASE;
      rememberApiBase(resolvedBase);
      return;
    }
  }

  for (const base of DEV_BACKEND_CANDIDATES) {
    if (await probe(base)) {
      resolvedBase = base;
      rememberApiBase(base);
      try { console.info('[api] using dev base', base); } catch (_) { }
      break;
    }
  }
}

const api = axios.create();

const REFRESH_TOKEN_KEY = 'refresh_token';
const HALISOFT_REFRESH_KEY = 'halisoft_refresh_token';
let refreshPromise = null; // Mutex: only one refresh at a time across all requests

api.interceptors.request.use(async (config) => {
  await ensureDevBase();
  if (resolvedBase && !/^\s*$/.test(resolvedBase)) config.baseURL = resolvedBase;
  try {
    // Check multiple token storage keys (main platform uses 'token', customer portal uses 'halisoft_token')
    const token = localStorage.getItem('token') || localStorage.getItem('halisoft_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // SECURITY: Add X-Requested-With for CSRF defense-in-depth
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
  } catch (_) {
    /* localStorage unavailable */
  }
  return config;
});

export default api;

async function tryRefreshAndRetry(originalConfig, attempt = 1) {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY) || localStorage.getItem(HALISOFT_REFRESH_KEY);
  if (!refreshToken) return null;

  const base = (typeof originalConfig?.baseURL === 'string' && originalConfig.baseURL)
    ? originalConfig.baseURL.replace(/\/+$/, '')
    : (resolvedBase || '');
  const refreshUrl = `${base}/api/auth/refresh`;
  const isRefreshRequest = (originalConfig?.url || '').includes('/auth/refresh');

  if (isRefreshRequest) return null;

  const doRefresh = async () => {
    const res = await fetch(refreshUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        refreshToken,
        refresh_attempt: attempt,
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, code: data?.code, message: data?.message };
    return { ok: true, token: data.token, refreshToken: data.refreshToken, user: data.user };
  };

  if (refreshPromise) await refreshPromise;
  refreshPromise = doRefresh();
  const result = await refreshPromise;
  refreshPromise = null;

  if (!result.ok) {
    if (result.code === 'REFRESH_TOKEN_ALREADY_USED' && attempt < 2) {
      await new Promise((r) => setTimeout(r, 400));
      const newToken = localStorage.getItem('token') || localStorage.getItem('halisoft_token');
      if (newToken) {
        originalConfig.headers = originalConfig.headers || {};
        originalConfig.headers.Authorization = `Bearer ${newToken}`;
        return api.request(originalConfig);
      }
    }
    return null;
  }

  try {
    localStorage.setItem('token', result.token);
    localStorage.setItem(REFRESH_TOKEN_KEY, result.refreshToken);
    if (localStorage.getItem('halisoft_token')) {
      localStorage.setItem('halisoft_token', result.token);
      localStorage.setItem(HALISOFT_REFRESH_KEY, result.refreshToken);
    }
    window.dispatchEvent(new CustomEvent('auth:tokenRefreshed', { detail: { token: result.token } }));
  } catch (_) {}
  originalConfig.headers = originalConfig.headers || {};
  originalConfig.headers.Authorization = `Bearer ${result.token}`;
  return api.request(originalConfig);
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const status = error?.response?.status;
    const url = (error?.config?.baseURL || '') + (error?.config?.url || '');
    try {
      const isRepoLatest = /\/api\/data\/[A-Za-z0-9_-]+\/latest\/?$/.test(error?.config?.url || '');
      const isRepoList = /\/api\/data\/[A-Za-z0-9_-]+\/list\/?$/.test(error?.config?.url || '');
      if (status === 404 && (isRepoLatest || isRepoList)) {
        return Promise.resolve({
          ...error.response,
          status: 200,
          data: isRepoLatest ? { name: null, url: null } : [],
          config: error.config,
          request: error.request,
        });
      }
    } catch (_) { }
    if (status === 401) {
      // Don't auto-logout for health check endpoints, auth check, or optional endpoints
      const url = error?.config?.url || '';
      console.log('[API] 401 error on:', url, 'token present:', !!localStorage.getItem('token'));

      // Never show session expired popup on login/signup/auth pages
      let isOnAuthPage = false;
      try {
        const path = window.location?.pathname || '';
        isOnAuthPage = /\/(login|signup|register|forgot-password)/i.test(path);
      } catch (_) { }
      if (isOnAuthPage) {
        try { localStorage.removeItem('token'); } catch (_) { }
        return Promise.reject(error);
      }

      const hasRefresh = !!(localStorage.getItem(REFRESH_TOKEN_KEY) || localStorage.getItem(HALISOFT_REFRESH_KEY));
      const isRefreshEndpoint = url.includes('/auth/refresh');
      if (hasRefresh && !isRefreshEndpoint) {
        try {
          const retried = await tryRefreshAndRetry(error.config);
          if (retried) return retried;
        } catch (refreshErr) {
          console.warn('[API] Refresh failed:', refreshErr?.message || refreshErr);
        }
      }

      // Don't show popup if there's no token (user is not logged in)
      let hasToken = false;
      try {
        hasToken = !!localStorage.getItem('token') || !!localStorage.getItem('halisoft_token');
      } catch (_) { }
      if (!hasToken) {
        return Promise.reject(error);
      }

      // Don't auto-logout if user just logged in (within last 5 seconds)
      const justLoggedIn = sessionStorage.getItem('just_logged_in');
      if (justLoggedIn) {
        console.log('[API] Skipping auto-logout - user just logged in');
        return Promise.reject(error);
      }

      let isDemoToken = false;
      try {
        isDemoToken = localStorage.getItem('token') === DEMO_TOKEN_KEY;
      } catch (_) { }
      const isExcludedEndpoint = url.includes('/test-connection') ||
        url.includes('/chat/health') ||
        url.includes('/ai-status') ||
        url.includes('/chat/active-sessions') ||
        url.includes('/chat/messages') ||
        url.includes('/chat/history') ||
        url.includes('/chat/guest/') ||
        url.includes('/support/chat/guest/') ||
        url.includes('/check-auth') ||
        url.includes('/white-label/') ||
        url.includes('/auth/me') ||
        url.includes('/api/opportunities') ||
        url.includes('/api/termsheets/') ||
        url.includes('/api/validation/') ||
        url.includes('/api/leads/') ||
        url.includes('/api/trade-partners/') ||
        url.includes('/api/permissions/') ||
        url.includes('/api/module-permissions/') ||
        url.includes('/api/haliblog/');

      if (!isExcludedEndpoint && !isDemoToken) {
        const debugInfo = !isProd ? `<br><small style="color:#888">Endpoint: ${url}</small>` : '';
        Swal.fire({
          icon: 'warning',
          title: 'Session Expired',
          html: `Your session has expired. You will be redirected to login.${debugInfo}`,
          confirmButtonText: 'Go to Login',
          showCancelButton: true,
          cancelButtonText: 'Report this bug',
          confirmButtonColor: '#2563EB',
          cancelButtonColor: '#6B7280',
          allowOutsideClick: false,
        }).then((result) => {
          if (!result.isConfirmed) {
            const subject = encodeURIComponent('Bug Report: Session Expired');
            const body = encodeURIComponent(`Hi,\n\nI encountered a session expiry issue.\n\nEndpoint: ${url}\nTime: ${new Date().toISOString()}\nBrowser: ${navigator.userAgent}\n`);
            window.open(`mailto:support@halisoft.biz?subject=${subject}&body=${body}`);
          }
        });
        try {
          localStorage.removeItem('token');
          localStorage.removeItem(REFRESH_TOKEN_KEY);
          localStorage.removeItem('halisoft_token');
          localStorage.removeItem(HALISOFT_REFRESH_KEY);
        } catch (_) { }
        try {
          window.dispatchEvent(new CustomEvent('auth:loggedOut'));
          window.dispatchEvent(new CustomEvent('navigate:auth'));
        } catch (_) { }
      }
    }
    if (status === 403) {
      const endpoint = error?.config?.url || 'unknown';
      const method = String(error?.config?.method || 'get').toLowerCase();
      // Skip the popup when the request opted out (ambient/best-effort calls
      // from contexts and layouts that fire on every page) or when the
      // endpoint is on the known "may-403-during-normal-use" list. The popup
      // was firing on /landing and other public pages because background
      // calls like /api/white-label/* return 403 for users with stale or
      // tenant-mismatched tokens.
      const skipPopup = error?.config?.silent403 === true;
      const ambientEndpointPatterns = [
        '/api/white-label/',
        '/api/check-auth',
        '/api/notifications',
        '/api/subscriptions/current',
      ];
      const isAmbient = ambientEndpointPatterns.some((p) => endpoint.includes(p));
      // Background reads (GET) should never raise an Access Denied modal —
      // they're page-mount fetches, not user-initiated actions. Only show
      // the popup for mutations (POST/PUT/PATCH/DELETE), which correspond
      // to explicit user clicks.
      const isBackgroundRead = method === 'get';
      if (skipPopup || isAmbient || isBackgroundRead) {
        if (!isProd) {
          console.warn('[api] 403 silenced for', method.toUpperCase(), endpoint);
        }
        return Promise.reject(error);
      }
      const debugInfo = !isProd ? `<br><small style="color:#888">Endpoint: ${endpoint}</small>` : '';
      Swal.fire({
        icon: 'error',
        title: 'Access Denied',
        html: `You don't have permission for this action.<br>Please contact your administrator.${debugInfo}`,
        confirmButtonText: 'OK',
        showCancelButton: true,
        cancelButtonText: 'Report this bug',
        showDenyButton: true,
        denyButtonText: 'Contact Support',
        denyButtonColor: '#2563EB',
        confirmButtonColor: '#6B7280',
        cancelButtonColor: '#9CA3AF',
      }).then((result) => {
        if (result.isDenied) {
          const subject = encodeURIComponent('Access Denied Issue');
          const body = encodeURIComponent(`Hi,\n\nI got an "Access Denied" error.\n\nEndpoint: ${endpoint}\nTime: ${new Date().toISOString()}\nBrowser: ${navigator.userAgent}\n`);
          window.open(`mailto:support@halisoft.biz?subject=${subject}&body=${body}`);
        } else if (result.dismiss === Swal.DismissReason.cancel) {
          const subject = encodeURIComponent('Bug Report: Unexpected Access Denied');
          const body = encodeURIComponent(`Hi,\n\nI believe I should have access to this feature but got denied.\n\nEndpoint: ${endpoint}\nTime: ${new Date().toISOString()}\nBrowser: ${navigator.userAgent}\n`);
          window.open(`mailto:support@halisoft.biz?subject=${subject}&body=${body}`);
        }
      });
    }
    return Promise.reject(error);
  }
);

export function axiosErrorMessage(error, fallback = 'Request failed') {
  return formatAxiosError(error, fallback);
}

// Export getter for resolved base URL (useful for building document URLs)
export const getApiBaseUrl = () => resolvedBase || '';
